// Mappa Leaflet con marker raggruppati per marker_group_key.
// Pin come divIcon SVG (no asset immagine): colore + icona seguono la
// categoria principale del gruppo, coerenti col sistema icone di filtri/upload.

import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { PhotoSummary } from "./api";
import { icon as svgIcon } from "./icons";
import { CATEGORIES } from "./categories";

export function createMap(el: HTMLElement): L.Map {
  const map = L.map(el, { zoomControl: true }).setView([41.9, 12.5], 5);
  L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    maxZoom: 19,
    attribution: "© OpenStreetMap",
  }).addTo(map);
  return map;
}

/** Disegna i marker (uno per gruppo) e collega il click. Ritorna il layer. */
export function renderMarkers(
  map: L.Map,
  photos: PhotoSummary[],
  onSelect: (group: PhotoSummary[]) => void,
): L.LayerGroup {
  const groups = new Map<string, PhotoSummary[]>();
  for (const p of photos) {
    const list = groups.get(p.marker_group_key);
    if (list) list.push(p);
    else groups.set(p.marker_group_key, [p]);
  }

  const layer = L.layerGroup().addTo(map);
  const bounds: [number, number][] = [];

  // Colore di default (nessuna categoria sul gruppo) dal tema attivo: solo
  // presentazione, nessun impatto sulla logica hasPrivate sottostante.
  const rootStyle = getComputedStyle(document.documentElement);
  const defaultColor = rootStyle.getPropertyValue("--md-marker").trim() || "#6b4c3b";
  const privateRing = rootStyle.getPropertyValue("--md-marker-private").trim() || "#c8935a";

  for (const items of groups.values()) {
    const { latitude, longitude } = items[0]; // coordinate esatte della prima foto
    // hasPrivate è true solo se il backend ha già incluso foto private nella
    // risposta, cioè solo a private mode già sbloccata: nessuna disclosure pre-unlock.
    const hasPrivate = items.some((p) => p.is_private === 1);
    // Categoria rappresentativa del gruppo: prima categoria della prima foto,
    // stessa convenzione usata per il riepilogo nel select multi-categoria.
    const cat = CATEGORIES.find((c) => c.value === items[0].categories?.[0]);
    const pinColor = cat?.color ?? defaultColor;
    const iconHtml = cat ? svgIcon(cat.icon, { size: 16, label: cat.label }) : "";
    const pin = L.divIcon({
      className: "map-pin-wrap",
      html: `<span class="map-pin${hasPrivate ? " map-pin--private" : ""}" style="background:${pinColor};${hasPrivate ? `border-color:${privateRing}` : ""}">${iconHtml}</span>`,
      iconSize: [32, 32],
      iconAnchor: [16, 16],
    });
    const marker = L.marker([latitude, longitude], { icon: pin }).addTo(layer);
    if (items.length > 1) marker.bindTooltip(String(items.length), { permanent: false });
    marker.on("click", () => onSelect(items));
    bounds.push([latitude, longitude]);
  }

  if (bounds.length) map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
  return layer;
}

/** Marker trascinabile per scegliere le coordinate in fase di upload (no asset). */
export function createPickMarker(
  map: L.Map,
  lat: number,
  lng: number,
  onMove: (lat: number, lng: number) => void,
): L.Marker {
  const pin = L.divIcon({
    className: "pick-pin",
    // Lo span interno porta l'animazione di "caduta" del pin: Leaflet anima il
    // contenitore esterno con il proprio transform di posizionamento, quindi
    // l'entrata animata va sullo span per non confliggere con quel transform.
    html: `<span class="pick-pin__bounce">${svgIcon("location", { size: 32, label: "Posizione scelta" })}</span>`,
    iconSize: [32, 32],
    iconAnchor: [16, 30],
  });
  const marker = L.marker([lat, lng], { draggable: true, icon: pin }).addTo(map);
  marker.on("dragend", () => {
    const p = marker.getLatLng();
    onMove(p.lat, p.lng);
  });
  return marker;
}
